import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocale } from "../contexts/LocaleContext";
import {
  Box,
  Button,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  LinearProgress,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useAuth } from "../contexts/AuthContext";
import { getSubscriptionStatus, cancelSubscription, createCheckoutSession } from "../utils/firebase";
import { PLANS, PAID_PLAN_IDS } from "../config/planConfig";

export default function BillingPage() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const { basePath } = useLocale();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [submittingPlanId, setSubmittingPlanId] = useState(null);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    setError(null);
    getSubscriptionStatus()
      .then((result) => setStatus(result.data))
      .catch((err) => setError(err.message || t("billingPage.failedLoad")))
      .finally(() => setLoading(false));
  }, [user, t]);

  const handleCancel = async () => {
    if (!window.confirm(t("billingPage.confirmCancel"))) return;
    setCancelling(true);
    setError(null);
    try {
      await cancelSubscription();
      const result = await getSubscriptionStatus();
      setStatus(result.data);
    } catch (err) {
      setError(err.message || t("billingPage.failedCancel"));
    } finally {
      setCancelling(false);
    }
  };

  const handleSubscribe = async (planId) => {
    if (!user || !PAID_PLAN_IDS.includes(planId)) return;
    setError(null);
    setSubmittingPlanId(planId);
    try {
      const appUrl = window.location.origin;
      const result = await createCheckoutSession({
        planId,
        returnUrl: `${appUrl}${basePath || ""}/checkout/success`,
        cancelUrl: `${appUrl}${basePath || ""}/checkout/cancel`,
      });
      if (result.data?.approvalUrl) {
        window.location.href = result.data.approvalUrl;
      } else {
        setError(t("checkout.errorCreateSession"));
      }
    } catch (err) {
      setError(err.message || t("checkout.errorStartCheckout"));
    } finally {
      setSubmittingPlanId(null);
    }
  };

  if (authLoading || !user) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  const planInfo = status ? PLANS.find((p) => p.planId === status.planId) || PLANS[0] : null;
  const isUnlimited = !!(status?.unlimited || status?.planId === "internal" || status?.callsLimit == null);
  const progress = !isUnlimited && status?.callsLimit
    ? Math.min(100, (status.callsUsed / status.callsLimit) * 100)
    : 0;
  const isPaidPlan = status?.planId && PAID_PLAN_IDS.includes(status.planId);

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box textAlign="center" mb={5}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Cal Sans", "Inter", sans-serif',
            fontWeight: 600,
            letterSpacing: "-0.02em",
          }}
        >
          {t("billingPage.choosePlan")}
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 1, fontSize: 17 }}>
          {t("billingPage.upgradeCancel")}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : status ? (
        <>
          <Card
            variant="outlined"
            sx={{
              mb: 5,
              borderRadius: 2,
              "& .MuiCardContent-root": { py: 2, "&:last-child": { pb: 2 } },
            }}
          >
            <CardContent>
              <Box display="flex" flexWrap="wrap" alignItems="center" gap={3}>
                <Box flex={1} minWidth={200}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("billingPage.currentPlan")}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="h6" fontWeight={600}>
                      {status.planId === "internal"
                        ? t("billingPage.plans.internal.name")
                        : planInfo
                        ? t(`billingPage.plans.${status.planId}.name`)
                        : status.planId}
                    </Typography>
                    {isUnlimited && (
                      <Chip
                        label={t("billingPage.internalAccount")}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
                <Box flex={1} minWidth={200}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("billingPage.usageThisPeriod")}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body1">
                      {isUnlimited
                        ? t("billingPage.unlimited")
                        : `${status.callsUsed} / ${status.callsLimit} ${t("billingPage.calls")}`}
                    </Typography>
                  </Box>
                  {!isUnlimited && (
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{ height: 6, borderRadius: 1, mt: 0.5 }}
                      color={progress >= 90 ? "error" : "primary"}
                    />
                  )}
                </Box>
                <Box>
                  {isPaidPlan && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleCancel}
                      disabled={cancelling}
                      startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {cancelling ? t("billingPage.cancelling") : t("billingPage.cancelSubscription")}
                    </Button>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          {!isUnlimited && (
          <Grid container spacing={3}>
            {PLANS.map((plan) => {
              const isCurrent = status.planId === plan.planId;
              const isSubmitting = submittingPlanId === plan.planId;
              const canSubscribe = plan.isPaid && !isCurrent;

              return (
                <Grid item xs={12} sm={6} lg={3} key={plan.planId}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 2,
                      borderColor: isCurrent ? "primary.main" : "divider",
                      borderWidth: isCurrent ? 2 : 1,
                      transition: "border-color 0.2s, box-shadow 0.2s",
                      "&:hover": canSubscribe
                        ? { borderColor: "primary.main", boxShadow: 1 }
                        : {},
                    }}
                  >
                    <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", pb: 2 }}>
                      <Typography variant="h6" fontWeight={600} gutterBottom>
                        {t(`billingPage.plans.${plan.planId}.name`)}
                      </Typography>
                      <Box mb={2}>
                        <Typography component="span" variant="h4" fontWeight={600}>
                          {plan.priceDisplay}
                        </Typography>
                        <Typography component="span" color="text.secondary" variant="body2" sx={{ ml: 0.5 }}>
                          /{t(`billingPage.plans.${plan.planId}.period`)}
                        </Typography>
                      </Box>
                      <List dense disablePadding sx={{ flex: 1, mb: 2 }}>
                        {plan.benefits.map((_, i) => (
                          <ListItem key={i} disableGutters sx={{ py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                              <CheckCircleIcon sx={{ fontSize: 18, color: "success.main" }} />
                            </ListItemIcon>
                            <Typography variant="body2" color="text.secondary">
                              {t(`billingPage.plans.${plan.planId}.benefit${i + 1}`)}
                            </Typography>
                          </ListItem>
                        ))}
                      </List>
                      {isCurrent ? (
                        <Chip label={t("billingPage.currentPlan")} color="primary" variant="outlined" size="small" sx={{ mt: "auto" }} />
                      ) : canSubscribe ? (
                        <Button
                          variant="contained"
                          fullWidth
                          onClick={() => handleSubscribe(plan.planId)}
                          disabled={isSubmitting}
                          startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                          sx={{ mt: "auto" }}
                        >
                          {isSubmitting ? t("billingPage.redirecting") : t("billingPage.subscribe")}
                        </Button>
                      ) : null}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          )}
        </>
      ) : null}
    </Container>
  );
}
